import { z } from "zod";
import type { Band, FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// income-tax — the shared, FY-versioned Australian income-tax / take-home
// engine. This is a FOUNDATIONAL package: other tools (HELP-not-withheld,
// take-home-pay, medicare-levy-reduction, LITO, SAPTO, non-resident,
// working-holiday-maker, part-year-resident) import these pure functions
// rather than re-deriving marginal-rate math.
//
// It is NOT a per-product ruleset like au06/au08 — it has no `product` code.
// Instead its rulesets carry an `engine: "income-tax"` tag plus the usual
// RuleSetMeta provenance fields. Calculation is pure: inputs -> rules ->
// outputs, assessment FY passed in, no Date.now(), no I/O.
// ---------------------------------------------------------------------------

/**
 * Tax residency status for the FY being assessed. This is the single switch
 * that selects which bracket schedule, threshold, levy, and offset rules apply.
 * The engine does NOT determine residency — that is a question of fact for the
 * ATO's residency tests. The caller supplies the status; copy frames it as
 * general information, never a residency determination.
 *
 * - `resident`  — Australian resident for tax purposes. Tax-free threshold,
 *                 resident marginal brackets, Medicare levy, LITO/SAPTO apply.
 * - `foreign`   — Foreign (non-) resident. No tax-free threshold (taxed from
 *                 $1), foreign-resident brackets, NO Medicare levy, no LITO/SAPTO.
 * - `whm`       — Working holiday maker (417/462). Dedicated WHM schedule
 *                 (15% to $45k), no tax-free threshold, NO Medicare levy.
 */
export const ResidencySchema = z.enum(["resident", "foreign", "whm"]);
export type Residency = z.infer<typeof ResidencySchema>;

/** A marginal tax bracket: `rate` applies to income in `[from, to)`. */
export type TaxBracket = Band;

/**
 * A study/training-loan (HELP/STSL) repayment band under the FY2025-26 marginal
 * system. Repayment is `baseAmount + rate × (income − thresholdFloor)` where
 * `thresholdFloor` is the band's `from - 1` (the ATO table reads "Nc for each
 * $1 over $X" and the band starts at $X+1) — UNLESS `wholeIncome`, in which case
 * `rate` is charged on the whole repayment income (the top "10% of total
 * repayment income" band, which is not marginal). Mirrors AU-06's RepaymentBand
 * so the two stay in lockstep; income-tax owns the shared copy.
 */
export interface HelpRepaymentBand {
  /** Lower bound of repayment income, inclusive. */
  from: number;
  /** Upper bound, exclusive. `null` = unbounded (top band). */
  to: number | null;
  /** Accumulated repayment at exactly `from` (whole dollars). */
  baseAmount: number;
  /** Marginal rate within the band (fraction, e.g. 0.15 = 15c/$1). */
  rate: number;
  /** When true, `rate` is charged on the WHOLE repayment income (top band). */
  wholeIncome?: boolean;
}

/** Low Income Tax Offset (LITO) shape — a capped, tapering, non-refundable,
 * resident-only offset. */
export interface LitoRules {
  /** Maximum offset at/below `taperStart` (e.g. $700). */
  max: number;
  /** Income at/below which the full `max` applies (e.g. $37,500). */
  taperStart: number;
  /** First taper rate, per $1 over `taperStart` (e.g. 0.05 = 5c/$1). */
  taper1Rate: number;
  /** Income at which the first taper ends / second begins (e.g. $45,000). */
  taper2Start: number;
  /** Second taper rate, per $1 over `taper2Start` (e.g. 0.015 = 1.5c/$1). */
  taper2Rate: number;
  /** Income at which the offset reaches $0 (e.g. $66,667). */
  cutOut: number;
}

/** Seniors and Pensioners Tax Offset (SAPTO) shape, single-status path.
 * Couple transfer math is out of scope for this engine version — only the
 * single shade-out/cut-out is modelled, which is the figure the ledger
 * verified for the foreground use case. */
export interface SaptoRules {
  /** Maximum offset, single (e.g. $2,230). */
  maxSingle: number;
  /** Rebate income at/below which the full offset applies (shade-out start). */
  shadeOutSingle: number;
  /** Rebate income at which the offset reaches $0 (cut-out). */
  cutOutSingle: number;
  /** Taper per $1 over the shade-out threshold (e.g. 0.125 = 12.5c/$1). */
  taperRate: number;
}

/** Medicare levy rules — rate + low-income reduction (resident-only). */
export interface MedicareLevyRules {
  /** Standard levy rate (e.g. 0.02 = 2%). */
  rate: number;
  /** Reduction shade-in rate per $1 over the lower threshold (e.g. 0.10). */
  reductionRate: number;
  /** Single low-income lower threshold — nil levy at/below (e.g. $28,011). */
  singleLowerThreshold: number;
  /** Single low-income upper threshold — full levy at/above. Derived as
   * `singleLowerThreshold / (1 - rate/reductionRate)`; ATO publishes it as
   * `lower × 1.25` for a 2% rate + 10c shade-in. */
  singleUpperThreshold: number;
  /** Family low-income lower threshold (no dependants). */
  familyLowerThreshold: number;
  /** Family low-income upper threshold (no dependants). */
  familyUpperThreshold: number;
  /** Per-dependant uplift to the family LOWER threshold. */
  familyChildLowerIncrement: number;
  /** Per-dependant uplift to the family UPPER threshold. */
  familyChildUpperIncrement: number;
  /** SAPTO/pensioner single low-income lower threshold. */
  saptoSingleLowerThreshold: number;
  /** SAPTO/pensioner single low-income upper threshold. */
  saptoSingleUpperThreshold: number;
}

/** Part-year resident pro-rated tax-free threshold (resident-only). */
export interface PartYearThresholdRules {
  /** The flat base everyone gets (e.g. $13,464). */
  base: number;
  /** The pro-rated remainder, apportioned by resident months (e.g. $4,736). */
  proRated: number;
}

/** A complete FY income-tax ruleset, keyed by financial year. */
export interface IncomeTaxRuleSet {
  /** Engine tag — distinguishes shared engines from per-product rulesets. */
  engine: "income-tax";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /** Tax-free threshold for a full-year resident (e.g. $18,200). */
  taxFreeThreshold: number;
  /** Resident marginal brackets, covering [0, ∞). */
  residentBrackets: readonly TaxBracket[];
  /** Foreign-resident marginal brackets (no tax-free threshold), [0, ∞). */
  foreignBrackets: readonly TaxBracket[];
  /** Working-holiday-maker brackets (15% from $1), [0, ∞). */
  whmBrackets: readonly TaxBracket[];
  /** Medicare levy + low-income reduction (residents only). */
  medicareLevy: MedicareLevyRules;
  /** LITO (residents only). */
  lito: LitoRules;
  /** SAPTO, single path (residents only). */
  sapto: SaptoRules;
  /** Part-year resident pro-rated tax-free threshold. */
  partYearThreshold: PartYearThresholdRules;
  /** HELP/STSL minimum repayment income — nil below this. */
  helpMinThreshold: number;
  /** HELP/STSL marginal repayment bands, covering [0, ∞). */
  helpBands: readonly HelpRepaymentBand[];
}

// ---------------------------------------------------------------------------
// Calculation inputs / outputs
// ---------------------------------------------------------------------------

/**
 * Filing/household context for the Medicare-levy low-income reduction and SAPTO.
 * Defaults to a single non-senior with no dependants — the common case.
 */
export const HouseholdSchema = z.object({
  /** Has a spouse and/or dependants → family low-income thresholds apply. */
  family: z.boolean().default(false),
  /** Number of dependent children (drives the per-child threshold uplift). */
  dependants: z.number().int().min(0).default(0),
  /** Eligible for SAPTO (reached Age Pension age + pension-eligible). The
   * engine does not test eligibility — the caller asserts it. */
  seniorPensioner: z.boolean().default(false),
});
export type Household = z.infer<typeof HouseholdSchema>;

export const IncomeTaxInputSchema = z.object({
  /** Taxable income for the FY (assessable income minus deductions). */
  taxableIncome: z.number().min(0),
  /** Residency status for the FY. Defaults to resident. */
  residency: ResidencySchema.default("resident"),
  /**
   * Number of months (inclusive of the arrival month) the person was a
   * resident, for the part-year pro-rated tax-free threshold. `null` / omitted
   * = full-year (whatever the residency status is). 1–11 triggers the
   * pro-rated threshold; 12 is treated as full-year. Resident-only.
   */
  residentMonths: z.number().int().min(1).max(12).nullable().default(null),
  /** Household context for Medicare-levy reduction + SAPTO. */
  household: HouseholdSchema.default({
    family: false,
    dependants: 0,
    seniorPensioner: false,
  }),
  /**
   * Repayment income for HELP/STSL, if different from taxable income (it adds
   * back RFB, net investment loss, etc.). Defaults to taxableIncome when
   * omitted. Pass 0-or-more; the engine never derives it from the clock.
   */
  repaymentIncome: z.number().min(0).optional(),
  /** Whether the person has an outstanding HELP/STSL loan (gates the
   * compulsory-repayment line). Defaults to false. */
  hasStudyLoan: z.boolean().default(false),
  /** The financial year being assessed, e.g. "2025-26". Passed in (never from
   * the clock) so historical re-runs resolve against the right ruleset. */
  assessmentYear: z.string(),
});
export type IncomeTaxInput = z.input<typeof IncomeTaxInputSchema>;
export type IncomeTaxParsed = z.infer<typeof IncomeTaxInputSchema>;

export interface IncomeTaxOutput {
  /** Taxable income the calculation ran against. */
  taxableIncome: number;
  /** Residency status applied. */
  residency: Residency;
  /** The tax-free threshold actually applied (0 for foreign/whm, pro-rated for
   * part-year residents, full for full-year residents). */
  taxFreeThreshold: number;
  /** Gross income tax before offsets and levy (whole dollars). */
  incomeTax: number;
  /** Medicare levy after any low-income reduction (whole dollars, 0 for
   * foreign/whm). */
  medicareLevy: number;
  /** LITO applied (resident-only, capped at the tax it can offset). */
  lito: number;
  /** SAPTO applied (resident senior/pensioner only, capped). */
  sapto: number;
  /** Net tax = max(0, incomeTax − lito − sapto) + medicareLevy. */
  netTax: number;
  /** Compulsory HELP/STSL repayment for the FY (0 if no loan or below
   * threshold). */
  helpRepayment: number;
  /** The HELP/STSL repayment income the band math used. */
  helpRepaymentIncome: number;
  /** Take-home (after-tax) income = taxableIncome − netTax − helpRepayment. */
  takeHome: number;
  /** Marginal income-tax rate on the next dollar (bracket rate; excludes
   * levy and HELP). */
  marginalRate: number;
}
